import { forwardRef, type ComponentPropsWithoutRef, type ElementRef, type Ref } from 'react'
import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area'
import { cn } from '@/lib/utils'

/**
 * ScrollArea — область прокрутки с аккуратной полосой из токенов ДС вместо системной (на Windows
 * системная полоса широкая, со стрелками, и в модалке рисуется у края окна рядом с карточкой). На
 * Radix (`@radix-ui/react-scroll-area`), как остальные примитивы ДС: прокрутка колесом, тачем и
 * клавиатурой остаётся нативной — стилизуется только полоса.
 *
 * Высоту ограничивает родитель или `className` (`max-h-96`, `flex-1 min-h-0` во flex-колонке):
 * корень — flex-колонка, вьюпорт сжимается под неё, лишнее прокручивается. Полоса видна, пока
 * содержимое не помещается (`type="auto"`), и становится заметнее при наведении.
 *
 * `orientation` — какие полосы нужны: вертикальная (по умолчанию), горизонтальная (широкие таблицы)
 * или обе. Внутри модального `Dialog` прокрутка работает: блокировка скролла страницы пропускает
 * прокручиваемые элементы внутри `DialogContent`, а вьюпорт — такой элемент.
 */
export interface ScrollAreaProps extends ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Root> {
  /** Направления прокрутки. */
  orientation?: 'vertical' | 'horizontal' | 'both'
  /** Классы вьюпорта — например, внутренние отступы содержимого. */
  viewportClassName?: string
  /** Ref вьюпорта (прокручиваемого элемента) — для программной прокрутки. */
  viewportRef?: Ref<HTMLDivElement>
}

export const ScrollArea = forwardRef<ElementRef<typeof ScrollAreaPrimitive.Root>, ScrollAreaProps>(
  (
    {
      className,
      children,
      orientation = 'vertical',
      type = 'auto',
      viewportClassName,
      viewportRef,
      ...props
    },
    ref,
  ) => (
    <ScrollAreaPrimitive.Root
      ref={ref}
      type={type}
      className={cn('relative flex flex-col overflow-hidden', className)}
      {...props}
    >
      <ScrollAreaPrimitive.Viewport
        ref={viewportRef}
        className={cn(
          'min-h-0 w-full flex-1 rounded-[inherit]',
          // Radix оборачивает содержимое в `display: table` (ради горизонтальной прокрутки). Без
          // горизонтальной полосы такая обёртка растягивается под широкое содержимое и обрезает
          // его — делаем её блочной, чтобы содержимое переносилось по ширине области.
          orientation === 'vertical' && '[&>div]:!block',
          viewportClassName,
        )}
      >
        {children}
      </ScrollAreaPrimitive.Viewport>
      {orientation !== 'horizontal' ? <ScrollBar orientation="vertical" /> : null}
      {orientation !== 'vertical' ? <ScrollBar orientation="horizontal" /> : null}
      <ScrollAreaPrimitive.Corner />
    </ScrollAreaPrimitive.Root>
  ),
)
ScrollArea.displayName = 'ScrollArea'

/** Полоса прокрутки: тонкая дорожка без фона и скруглённый бегунок из токенов границы/текста. */
export const ScrollBar = forwardRef<
  ElementRef<typeof ScrollAreaPrimitive.Scrollbar>,
  ComponentPropsWithoutRef<typeof ScrollAreaPrimitive.Scrollbar>
>(({ className, orientation = 'vertical', ...props }, ref) => (
  <ScrollAreaPrimitive.Scrollbar
    ref={ref}
    orientation={orientation}
    className={cn(
      'group flex touch-none select-none p-0.5 transition-colors',
      orientation === 'vertical' ? 'h-full w-2.5' : 'h-2.5 flex-col',
      className,
    )}
    {...props}
  >
    <ScrollAreaPrimitive.Thumb className="relative flex-1 rounded-full bg-line-strong transition-colors group-hover:bg-txt-tertiary" />
  </ScrollAreaPrimitive.Scrollbar>
))
ScrollBar.displayName = 'ScrollBar'
